import { useState, useRef, useEffect } from 'react';
import { Messages } from 'primereact/messages';
import { Toast } from 'primereact/toast';
import { ProgressSpinner } from 'primereact/progressspinner';
import { TabView, TabPanel } from 'primereact/tabview';
import { BlockUI } from 'primereact/blockui';
import { Sidebar } from 'primereact/sidebar';
import { ConfirmDialog, confirmDialog } from 'primereact/confirmdialog';
import { Dialog } from 'primereact/dialog';
import { Button } from 'primereact/button';
import { useForm } from 'react-hook-form';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { AppCard, AppPage } from '../../components/general';
import { CreateIntegrationButton } from './CreateIntegrationButton';
import { EditIntegrationButton } from './EditIntegrationButton';
import FormDropdown from '../../components/form/FormDropdown';
import FormInputText from '../../components/form/FormInputText';
import FormRadioGroup from '../../components/form/FormRadioGroup';
import { EnvironmentOptions } from '../../utils/constants';
import { EditSnapFloodIntegrationSettings } from './EditSnapFloodIntegrationSettings';
import { EditVestaLosIntegrationSettings } from './EditVestaLosIntegrationSettings';
import { EditFannieMaeUnderwriteIntegrationSettings } from './EditFannieMaeUnderwriteIntegrationSettings';
import { EditFreddieMacUnderwriteIntegrationSettings } from './EditFreddieMacUnderwriteIntegrationSettings';
import { EditTruvVoaIntegrationSettings } from './EditTruvVoaIntegrationSettings';
import { EditTruvVoieIntegrationSettings } from './EditTruvVoieIntegrationSettings';
import { IntegrationVendor, IntegrationType } from '../../models/enums/integrationEnums';
import { IntegrationSettings } from '../../models/integrationSettings';
import IntegrationSettingsService from '../../services/integrationSettingsService';
import { mapFrontendToBackendType } from '../../utils/integrationTypeMapping';
import { EditArgyleVoaIntegrationSettings } from './EditArgyleVoaIntegrationSettings';
import { EditArgyleVoieIntegrationSettings } from './EditArgyleVoieIntegrationSettings';
import { EditEquifaxVoieIntegrationSettings } from './EditEquifaxVoieIntegrationSettings';
import { EditTrueworkVoieIntegrationSettings } from './EditTrueworkVoieIntegrationSettings';
import { EditSmartFeeFeeIntegrationSettings } from './EditSmartFeeFeeIntegrationSettings';
import { EditErnstFeeIntegrationSettings } from './EditErnstFeeIntegrationSettings';
import { EditRatesBoardPricingIntegrationSettings } from './EditRatesBoardPricingIntegrationSettings';
import { EditFactualCreditIntegrationSettings } from './EditFactualCreditIntegrationSettings';
import { EditExperianVoieIntegrationSettings } from './EditExperianVoieIntegrationSettings';
import { VendorOverrideFields } from './VendorOverrideFields';

// Combined type for all possible form fields
type FormValues = {
  environment: string;
  vendor: string;
  name: string;
  lenderId?: string;
};

interface ConnectionSettingsProps {
  orgUnitId?: string;
  asPage?: boolean;
}

const ConnectionSettings = ({ orgUnitId, asPage = true }: ConnectionSettingsProps) => {
  const [isDeleting, setIsDeleting] = useState<boolean>(false);
  const messages = useRef<Messages>(null);
  const toast = useRef<Toast>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [integrationSettings, setIntegrationSettings] = useState<IntegrationSettings[]>([]);
  const [sidebarVisible, setSidebarVisible] = useState<boolean>(false);
  const [integrationToEdit, setIntegrationToEdit] = useState<IntegrationSettings | null>(null);
  const [integrationType, setIntegrationType] = useState<IntegrationType>(IntegrationType.Flood);
  const [vendorOptions, setVendorOptions] = useState<any[]>([]);
  const [activeTabIndex, setActiveTabIndex] = useState<number>(0);
  const [activeIntegrationType, setActiveIntegrationType] = useState<IntegrationType>(IntegrationType.LOS);
  const [vendorTypeMappings, setVendorTypeMappings] = useState<Record<string, any[]>>({});
  const [rootLevelSettings, setRootLevelSettings] = useState<IntegrationSettings[]>([]);
  const [selectedRootConnection, setSelectedRootConnection] = useState<IntegrationSettings | null>(null);
  const [sidebarTabIndex, setSidebarTabIndex] = useState<number>(0); // 0 = Create New, 1 = Override
  const isOrgUnitMode = !!orgUnitId;
  
  // Override field values and flags
  const [overrideValues, setOverrideValues] = useState<Record<string, string>>({});
  const [overrideFlags, setOverrideFlags] = useState<Record<string, boolean>>({});

  const validationSchema = yup.object().shape({
    environment: yup.string().required('Environment is required'),
    vendor: yup.string().required('Vendor selection is required'),
    name: yup.string().required('Integration name is required'),
    lenderId: yup.string().optional().default(undefined),
  });

  const formMethods = useForm<FormValues>({
    defaultValues: {
      environment: 'Test',
      vendor: '',
      name: '',
    },
    resolver: yupResolver<FormValues>(validationSchema),
    mode: 'onChange',
  });

  const { control, formState, handleSubmit, reset, watch } = formMethods;
  const { errors: formErrors } = formState;
  const selectedVendor = watch('vendor');

  const integrationSettingsService = IntegrationSettingsService.getInstance();

  const loadVendorTypeMappings = async () => {
    try {
      const mappings = await integrationSettingsService.getVendorTypeMappings();
      const mappingDict: Record<string, any[]> = {};
      mappings.forEach((item: any) => {
        const frontendType = Object.keys(IntegrationType).find(
          (key) => mapFrontendToBackendType(IntegrationType[key as keyof typeof IntegrationType]) === item.type
        );
        if (frontendType) {
          mappingDict[IntegrationType[frontendType as keyof typeof IntegrationType]] = item.vendors.map((v: any) => ({
            label: v,
            value: v,
          }));
        }
      });
      setVendorTypeMappings(mappingDict);
    } catch (error) {
      showError('Failed to load vendor mappings');
    }
  };

  useEffect(() => {
    // Reset to LOS tab when orgUnitId changes
    setActiveTabIndex(0);
    setActiveIntegrationType(IntegrationType.LOS);
    loadVendorTypeMappings();
    loadConnectionSettings(IntegrationType.LOS);
  }, [orgUnitId]);

  // Load settings when the active tab changes or orgUnitId changes
  useEffect(() => {
    if (activeIntegrationType) {
      loadConnectionSettings(activeIntegrationType);
    }
  }, [activeIntegrationType, orgUnitId]);

  const loadConnectionSettings = async (type: IntegrationType) => {
    try {
      if (
        !(
          type === IntegrationType.LOS ||
          type === IntegrationType.Flood ||
          type === IntegrationType.Underwrite ||
          type === IntegrationType.Credit ||
          type === IntegrationType.VOA ||
          type === IntegrationType.VOIE ||
          type === IntegrationType.Fees ||
          type === IntegrationType.Pricing
        )
      ) {
        return;
      }
      setIsLoading(true);
      const settings = await integrationSettingsService.getByType(type);
      
      console.log('loadConnectionSettings - isOrgUnitMode:', isOrgUnitMode);
      console.log('loadConnectionSettings - orgUnitId:', orgUnitId);
      console.log('loadConnectionSettings - all settings:', settings);
      
      // Filter based on mode
      const filteredSettings = isOrgUnitMode
        ? settings.filter((s) => {
            console.log('Checking setting:', s.name, 'orgUnitId:', s.orgUnitId, 'matches:', s.orgUnitId === orgUnitId);
            return s.orgUnitId === orgUnitId;
          })
        : settings.filter((s) => !s.orgUnitId);
      
      console.log('loadConnectionSettings - filtered settings:', filteredSettings);
      setIntegrationSettings(filteredSettings);
      
      // Store root level settings for org unit mode
      if (isOrgUnitMode) {
        const rootSettings = settings.filter((s) => !s.orgUnitId);
        setRootLevelSettings(rootSettings);
      }
    } catch (error) {
      showError(error instanceof Error ? error.message : `An error occurred while loading ${type} integration settings`);
    } finally {
      setIsLoading(false);
    }
  };

  const handleTabChange = (e: any) => {
    setActiveTabIndex(e.index);

    // Map tab index to integration type
    let newType: IntegrationType;
    switch (e.index) {
      case 0:
        newType = IntegrationType.LOS;
        break;
      case 1:
        newType = IntegrationType.Underwrite;
        break;
      case 2:
        newType = IntegrationType.Credit;
        break;
      case 3:
        newType = IntegrationType.VOA;
        break;
      case 4:
        newType = IntegrationType.VOIE;
        break;
      case 5:
        newType = IntegrationType.Title;
        break;
      case 6:
        newType = IntegrationType.Flood;
        break;
      case 7:
        newType = IntegrationType.Pricing;
        break;
      case 8:
        newType = IntegrationType.Fees;
        break;
      default:
        newType = IntegrationType.LOS;
    }

    setActiveIntegrationType(newType);
  };

  const showError = (error: string) => {
    messages.current?.show({ severity: 'error', summary: 'Error:', detail: error, life: 10000 });
  };

  const onEditClick = (setting: IntegrationSettings) => {
    // Set current integration to indicate edit mode
    setIntegrationToEdit(setting);

    // Set the integration type based on the setting
    setIntegrationType(activeIntegrationType);

    // Set vendor options based on the active integration type from dynamic mappings
    const options = vendorTypeMappings[activeIntegrationType] || [];
    setVendorOptions(options);

    // Check if this is an override in org unit mode
    if (isOrgUnitMode && setting.isOverride && setting.coreIntegrationSettingsId) {
      // Switch to Override tab
      setSidebarTabIndex(1);
      
      // Find and select the core integration setting
      const coreConnection = rootLevelSettings.find(
        (s) => s.integrationSettingsId === setting.coreIntegrationSettingsId
      );
      setSelectedRootConnection(coreConnection || null);
      
      // Parse the core integration data first
      let coreData: Record<string, string> = {};
      if (coreConnection?.integrationData) {
        try {
          coreData = typeof coreConnection.integrationData === 'string' 
            ? JSON.parse(coreConnection.integrationData) 
            : coreConnection.integrationData;
        } catch (error) {
          console.error('Error parsing core integration data:', error);
        }
      }
      
      // Parse the override values
      let overrideData: Record<string, string> = {};
      if (setting.integrationData) {
        try {
          overrideData = typeof setting.integrationData === 'string' 
            ? JSON.parse(setting.integrationData) 
            : setting.integrationData;
        } catch (error) {
          console.error('Error parsing override integration data:', error);
        }
      }
      
      // Merge core data with override data (override data takes precedence)
      const mergedValues = { ...coreData, ...overrideData };
      setOverrideValues(mergedValues);
      
      // Set override flags based on settingOverrides array
      if (setting.settingOverrides && Array.isArray(setting.settingOverrides)) {
        const flags: Record<string, boolean> = {};
        setting.settingOverrides.forEach((path) => {
          flags[path] = true;
        });
        setOverrideFlags(flags);
      } else {
        setOverrideFlags({});
      }
    } else {
      // Regular edit mode (not override)
      setSidebarTabIndex(0);
      
      // Pre-fill the form with existing values
      reset({
        environment: setting.environment || 'Test',
        vendor: setting.vendor,
        name: setting.name,
      });
    }

    // Show the sidebar
    setSidebarVisible(true);
  };

  const onDeleteClick = (setting: IntegrationSettings) => {
    confirmDialog({
      message: `Are you sure you want to delete the integration "${setting.name}"?`,
      header: 'Confirmation',
      icon: 'pi pi-exclamation-triangle',
      accept: async () => {
        setIsDeleting(true);
        try {
          await IntegrationSettingsService.getInstance().delete(setting.integrationSettingsId!);
          loadConnectionSettings(activeIntegrationType);
          toast.current?.show({
            severity: 'success',
            summary: 'Success',
            detail: 'Integration settings deleted successfully',
            life: 3000,
          });
        } catch (error: any) {
          showError(error instanceof Error ? error.message : 'An error occurred while deleting integration settings');
        } finally {
          setIsDeleting(false);
        }
      },
    });
  };

  const onCreateClick = (type: IntegrationType) => {
    setIntegrationType(type);
    setIntegrationToEdit(null);
    
    const options = vendorTypeMappings[type] || [];
    setVendorOptions(options);
    reset({ environment: 'Test', vendor: '', name: '' });
    
    // In org unit mode, start on Create New tab (index 0)
    if (isOrgUnitMode) {
      setSidebarTabIndex(0);
      setSelectedRootConnection(null);
    }
    
    setSidebarVisible(true);
  };

  const handleRootConnectionSelect = (connectionId: number) => {
    const connection = rootLevelSettings.find((s) => s.integrationSettingsId === connectionId);
    setSelectedRootConnection(connection || null);
    
    // Parse the core integration data and populate overrideValues
    if (connection?.integrationData) {
      try {
        const parsedData = typeof connection.integrationData === 'string' 
          ? JSON.parse(connection.integrationData) 
          : connection.integrationData;
        setOverrideValues(parsedData || {});
      } catch (error) {
        console.error('Error parsing integration data:', error);
        setOverrideValues({});
      }
    } else {
      setOverrideValues({});
    }
    
    setOverrideFlags({});
  };

  const handleOverrideValueChange = (field: string, value: string) => {
    setOverrideValues((prev) => ({ ...prev, [field]: value }));
  };

  const handleOverrideFlagChange = (field: string, checked: boolean) => {
    setOverrideFlags((prev) => ({ ...prev, [field]: checked }));
  };

  const handleSaveOverride = async () => {
    if (!selectedRootConnection) return;
    try {
      setIsSaving(true);
      
      // Build array of JSON paths for fields that are checked (overridden)
      const jsonPaths: string[] = [];
      const overriddenValuesOnly: Record<string, string> = {};
      
      Object.entries(overrideFlags).forEach(([key, isChecked]) => {
        if (isChecked) {
          jsonPaths.push(key);
          // Only include the value if it's been overridden
          if (overrideValues[key] !== undefined) {
            overriddenValuesOnly[key] = overrideValues[key];
          }
        }
      });
      
      // Convert only overridden values to JSON string for integrationData
      const integrationDataJson = JSON.stringify(overriddenValuesOnly);
      
      const isUpdate = !!integrationToEdit;
      
      if (isUpdate) {
        // Update existing override
        const updateSettings: IntegrationSettings = {
          integrationSettingsId: integrationToEdit.integrationSettingsId,
          name: selectedRootConnection.name, // Use exact same name as core setting
          vendor: selectedRootConnection.vendor,
          status: selectedRootConnection.status,
          environment: selectedRootConnection.environment,
          integrationData: integrationDataJson, // Only overridden values as JSON string
          orgUnitId: orgUnitId,
          coreIntegrationSettingsId: selectedRootConnection.integrationSettingsId,
          settingOverrides: jsonPaths.length > 0 ? jsonPaths : undefined, // Array of JSON paths
        };
        await integrationSettingsService.update(updateSettings);
      } else {
        // Create new override
        const newSettings: IntegrationSettings = {
          name: selectedRootConnection.name, // Use exact same name as core setting
          vendor: selectedRootConnection.vendor,
          status: selectedRootConnection.status,
          environment: selectedRootConnection.environment,
          integrationData: integrationDataJson, // Only overridden values as JSON string
          orgUnitId: orgUnitId,
          coreIntegrationSettingsId: selectedRootConnection.integrationSettingsId,
          settingOverrides: jsonPaths.length > 0 ? jsonPaths : undefined, // Array of JSON paths
        };
        await integrationSettingsService.create(newSettings);
      }
      
      // Reload the integration settings to show the updated/created tile
      await loadConnectionSettings(integrationType);
      
      toast.current?.show({
        severity: 'success',
        summary: 'Success',
        detail: `Connection override ${isUpdate ? 'updated' : 'created'} successfully`,
        life: 3000,
      });
      setSidebarVisible(false);
      setSelectedRootConnection(null);
      setIntegrationToEdit(null);
      setOverrideValues({});
      setOverrideFlags({});
    } catch (error) {
      showError(error instanceof Error ? error.message : `An error occurred while ${integrationToEdit ? 'updating' : 'creating'} override`);
    } finally {
      setIsSaving(false);
    }
  };

  const onSidebarHide = () => {
    setSidebarVisible(false);
    setIntegrationToEdit(null);
    reset({ environment: 'Test', vendor: '', name: '' });
  };

  const handleSaveIntegration = async (settings: any) => {
    // Validate the entire parent form before saving
    const isValid = await formMethods.trigger();
    if (isValid) {
      try {
        setIsSaving(true);

        // Update the settings with the name, vendor, and environment from the parent form
        const updatedSettings = {
          ...settings,
          name: formMethods.getValues('name'),
          vendor: formMethods.getValues('vendor'),
          environment: formMethods.getValues('environment'),
          orgUnitId: orgUnitId || undefined,
        };
        console.log('Saving integration settings:', updatedSettings);

        if (updatedSettings.integrationSettingsId) {
          await integrationSettingsService.update(updatedSettings);
        } else {
          await integrationSettingsService.create(updatedSettings);
        }

        toast.current?.show({
          severity: 'success',
          summary: 'Success',
          detail: 'Integration settings saved successfully',
          life: 3000,
        });

        // Close the sidebar after saving
        onSidebarHide();

        // Reload the integration settings to show the newly created integration
        loadConnectionSettings(activeIntegrationType);
      } catch (error) {
        console.error('Error saving integration settings:', error);
        showError(error instanceof Error ? error.message : 'An error occurred while saving integration settings');
      } finally {
        setIsSaving(false);
      }
    }
  };

  const onSubmit = handleSubmit((data: FormValues) => {
    console.log('Form data:', data);
    // Continue with selected vendor - validation will prevent submission if name is empty
    setSidebarVisible(false);
    reset({ environment: 'Test', vendor: '', name: '' });
  });

  const getRootConnectionsByType = (type: IntegrationType) => {
    const vendors = vendorTypeMappings[type]?.map((v) => v.value) || [];
    return rootLevelSettings.filter((s) => vendors.includes(s.vendor));
  };

  const renderVendorSpecificSettings = () => (
    <>
      {integrationType === IntegrationType.Flood && selectedVendor === IntegrationVendor.Snap && (
        <div className="mt-4">
          <EditSnapFloodIntegrationSettings
            onCancel={integrationToEdit ? onSidebarHide : () => reset({ environment: 'Test', vendor: '', name: '' })}
            onSave={handleSaveIntegration}
            integrationSettings={integrationToEdit || undefined}
            environment={watch('environment')}
          />
        </div>
      )}

      {integrationType === IntegrationType.LOS && selectedVendor === IntegrationVendor.Vesta && (
        <div className="mt-4">
          <EditVestaLosIntegrationSettings
            onCancel={integrationToEdit ? onSidebarHide : () => reset({ environment: 'Test', vendor: '', name: '' })}
            onSave={handleSaveIntegration}
            integrationSettings={integrationToEdit || undefined}
            environment={watch('environment')}
          />
        </div>
      )}

      {integrationType === IntegrationType.Underwrite && selectedVendor === IntegrationVendor.FannieMae && (
        <div className="mt-4">
          <EditFannieMaeUnderwriteIntegrationSettings
            onCancel={integrationToEdit ? onSidebarHide : () => reset({ environment: 'Test', vendor: '', name: '' })}
            onSave={handleSaveIntegration}
            integrationSettings={integrationToEdit || undefined}
            environment={watch('environment')}
          />
        </div>
      )}

      {integrationType === IntegrationType.Underwrite && selectedVendor === IntegrationVendor.FreddieMac && (
        <div className="mt-4">
          <EditFreddieMacUnderwriteIntegrationSettings
            onCancel={integrationToEdit ? onSidebarHide : () => reset({ environment: 'Test', vendor: '', name: '' })}
            onSave={handleSaveIntegration}
            integrationSettings={integrationToEdit || undefined}
            environment={watch('environment')}
          />
        </div>
      )}

      {integrationType === IntegrationType.VOA && selectedVendor === IntegrationVendor.Truv && (
        <div className="mt-4">
          <EditTruvVoaIntegrationSettings
            onCancel={integrationToEdit ? onSidebarHide : () => reset({ environment: 'Test', vendor: '', name: '' })}
            onSave={handleSaveIntegration}
            integrationSettings={integrationToEdit || undefined}
            environment={watch('environment')}
          />
        </div>
      )}

      {integrationType === IntegrationType.VOIE && selectedVendor === IntegrationVendor.Truv && (
        <div className="mt-4">
          <EditTruvVoieIntegrationSettings
            onCancel={integrationToEdit ? onSidebarHide : () => reset({ environment: 'Test', vendor: '', name: '' })}
            onSave={handleSaveIntegration}
            integrationSettings={integrationToEdit || undefined}
            environment={watch('environment')}
          />
        </div>
      )}

      {integrationType === IntegrationType.VOIE && selectedVendor === IntegrationVendor.Equifax && (
        <div className="mt-4">
          <EditEquifaxVoieIntegrationSettings
            onCancel={integrationToEdit ? onSidebarHide : () => reset({ environment: 'Test', vendor: '', name: '' })}
            onSave={handleSaveIntegration}
            integrationSettings={integrationToEdit || undefined}
            environment={watch('environment')}
          />
        </div>
      )}

      {integrationType === IntegrationType.VOA && selectedVendor === IntegrationVendor.Argyle && (
        <div className="mt-4">
          <EditArgyleVoaIntegrationSettings
            onCancel={integrationToEdit ? onSidebarHide : () => reset({ environment: 'Test', vendor: '', name: '' })}
            onSave={handleSaveIntegration}
            integrationSettings={integrationToEdit || undefined}
            environment={watch('environment')}
          />
        </div>
      )}

      {integrationType === IntegrationType.VOIE && selectedVendor === IntegrationVendor.Argyle && (
        <div className="mt-4">
          <EditArgyleVoieIntegrationSettings
            onCancel={integrationToEdit ? onSidebarHide : () => reset({ environment: 'Test', vendor: '', name: '' })}
            onSave={handleSaveIntegration}
            integrationSettings={integrationToEdit || undefined}
            environment={watch('environment')}
          />
        </div>
      )}

      {integrationType === IntegrationType.VOIE && selectedVendor === IntegrationVendor.Truework && (
        <div className="mt-4">
          <EditTrueworkVoieIntegrationSettings
            onCancel={integrationToEdit ? onSidebarHide : () => reset({ environment: 'Test', vendor: '', name: '' })}
            onSave={handleSaveIntegration}
            integrationSettings={integrationToEdit || undefined}
            environment={watch('environment')}
          />
        </div>
      )}

      {integrationType === IntegrationType.Fees && selectedVendor === IntegrationVendor.SmartFees && (
        <div className="mt-4">
          <EditSmartFeeFeeIntegrationSettings
            onCancel={integrationToEdit ? onSidebarHide : () => reset({ environment: 'Test', vendor: '', name: '' })}
            onSave={handleSaveIntegration}
            integrationSettings={integrationToEdit || undefined}
            environment={watch('environment')}
          />
        </div>
      )}

      {integrationType === IntegrationType.Fees && selectedVendor === IntegrationVendor.Ernst && (
        <div className="mt-4">
          <EditErnstFeeIntegrationSettings
            onCancel={integrationToEdit ? onSidebarHide : () => reset({ environment: 'Test', vendor: '', name: '' })}
            onSave={handleSaveIntegration}
            integrationSettings={integrationToEdit || undefined}
            environment={watch('environment')}
          />
        </div>
      )}

      {integrationType === IntegrationType.Pricing && selectedVendor === IntegrationVendor.RatesBoard && (
        <div className="mt-4">
          <EditRatesBoardPricingIntegrationSettings
            onCancel={integrationToEdit ? onSidebarHide : () => reset({ environment: 'Test', vendor: '', name: '' })}
            onSave={handleSaveIntegration}
            integrationSettings={integrationToEdit || undefined}
            environment={watch('environment')}
          />
        </div>
      )}

      {integrationType === IntegrationType.Credit && selectedVendor === IntegrationVendor.Factual && (
        <div className="mt-4">
          <EditFactualCreditIntegrationSettings
            onCancel={integrationToEdit ? onSidebarHide : () => reset({ environment: 'Test', vendor: '', name: '' })}
            onSave={handleSaveIntegration}
            integrationSettings={integrationToEdit || undefined}
            environment={watch('environment')}
          />
        </div>
      )}

      {integrationType === IntegrationType.VOIE && selectedVendor === IntegrationVendor.Experian && (
        <div className="mt-4">
          <EditExperianVoieIntegrationSettings
            onCancel={integrationToEdit ? onSidebarHide : () => reset({ environment: 'Test', vendor: '', name: '' })}
            onSave={handleSaveIntegration}
            integrationSettings={integrationToEdit || undefined}
            environment={watch('environment')}
          />
        </div>
      )}

      {(!selectedVendor || selectedVendor === '') && (
        <form onSubmit={onSubmit}>
          <div className="flex justify-content-end gap-2 mt-3">
            <button
              type="button"
              className="p-button p-button-outlined"
              style={{ width: 'auto', minWidth: 'auto' }}
              onClick={onSidebarHide}
            >
              Cancel
            </button>
            <button type="submit" className="p-button" style={{ width: 'auto', minWidth: 'auto' }}>
              Next
              <i className="pi pi-arrow-right ml-2"></i>
            </button>
          </div>
        </form>
      )}
    </>
  );

  const renderTabContent = (integrationType: IntegrationType) => (
    <div className="grid gap-5">
      <div className="col-12">
        <div className="grid">
          {integrationSettings.map((setting) => (
            <div key={setting.integrationSettingsId} className="col-12 md:col-4 lg:col-3 xl:col-2 mb-3">
              <BlockUI blocked={isDeleting} fullScreen={false} style={{ borderRadius: '12px' }}>
                <EditIntegrationButton
                  integrationName={setting.name}
                  vendorName={setting.vendor}
                  onClick={() => {
                    onEditClick(setting);
                  }}
                  onDelete={() => onDeleteClick(setting)}
                />
              </BlockUI>
            </div>
          ))}

          <div className="col-12 md:col-4 lg:col-3 xl:col-2 mb-3">
            <CreateIntegrationButton onClick={() => onCreateClick(integrationType)} />
          </div>
        </div>
      </div>
    </div>
  );

  const content = (
    <>
      <Toast ref={toast} />
      <Messages ref={messages} />
      <ConfirmDialog />
        {isLoading ? (
          <div className="flex justify-content-center">
            <ProgressSpinner />
          </div>
        ) : (
          <>
            <BlockUI
              blocked={isLoading}
              template={
                <div className="flex justify-content-center">
                  <ProgressSpinner />
                </div>
              }
            >
              <div className="grid gap-5">
                <div className="col-12">
                  <TabView activeIndex={activeTabIndex} onTabChange={handleTabChange}>
                    <TabPanel header="LOS">{renderTabContent(IntegrationType.LOS)}</TabPanel>
                    <TabPanel header="Underwrite">{renderTabContent(IntegrationType.Underwrite)}</TabPanel>
                    <TabPanel header="Credit">{renderTabContent(IntegrationType.Credit)}</TabPanel>
                    <TabPanel header="VOA">{renderTabContent(IntegrationType.VOA)}</TabPanel>
                    <TabPanel header="VOIE">{renderTabContent(IntegrationType.VOIE)}</TabPanel>
                    <TabPanel header="Title">
                      <div
                        className="flex flex-column align-items-center justify-content-center"
                        style={{ minHeight: '200px', textAlign: 'center' }}
                      >
                        <i className="pi pi-clock" style={{ fontSize: '3rem', color: '#888' }}></i>
                        <h4 style={{ margin: '1rem 0 0.5rem 0' }}>Coming Soon</h4>
                        <p style={{ color: '#888' }}>Title integration settings will be available in a future update.</p>
                      </div>
                    </TabPanel>
                    <TabPanel header="Flood">{renderTabContent(IntegrationType.Flood)}</TabPanel>
                    <TabPanel header="Pricing">{renderTabContent(IntegrationType.Pricing)}</TabPanel>
                    <TabPanel header="Fee">{renderTabContent(IntegrationType.Fees)}</TabPanel>
                  </TabView>
                </div>
              </div>
            </BlockUI>

            <Sidebar
              visible={sidebarVisible}
              position="right"
              onHide={onSidebarHide}
              className="p-sidebar-md"
              header={integrationToEdit ? `Edit ${integrationToEdit.name}` : `Create ${integrationType} Integration`}
              blockScroll
            >
              <BlockUI
                blocked={isSaving}
                template={
                  <div className="flex justify-content-center">
                    <ProgressSpinner />
                  </div>
                }
              >
                {isOrgUnitMode ? (
                  <TabView activeIndex={sidebarTabIndex} onTabChange={(e) => setSidebarTabIndex(e.index)}>
                    <TabPanel header="Create New">
                      <div className="p-3 p-fluid p-input-filled">
                        {/* Environment selection form */}
                        <div className="field mb-4">
                          <FormRadioGroup
                            control={control}
                            errors={formErrors}
                            field="environment"
                            label="Environment *"
                            options={EnvironmentOptions}
                          />
                        </div>

                        {/* Vendor selection form */}
                        <div className="field mb-4">
                          <FormDropdown
                            className="field"
                            control={control}
                            errors={formErrors}
                            field="vendor"
                            label={integrationType === IntegrationType.Fees ? 'Select a Fee Provider *' : 'Select a Vendor *'}
                            options={vendorOptions}
                            placeholder="Select a vendor"
                            showClear={true}
                          />
                        </div>

                        <div className="field mb-4">
                          <FormInputText
                            control={control}
                            errors={formErrors}
                            field="name"
                            label="Integration Name *"
                            placeholder="Enter a name for this integration"
                          />
                        </div>

                        {renderVendorSpecificSettings()}
                      </div>
                    </TabPanel>
                    <TabPanel header="Override">
                      <div className="p-3 p-fluid p-input-filled">
                        <div className="field mb-4">
                          <label htmlFor="rootConnection">Select Connection to Override *</label>
                          <select
                            id="rootConnection"
                            className="p-inputtext p-component"
                            value={selectedRootConnection?.integrationSettingsId || ''}
                            onChange={(e) => handleRootConnectionSelect(Number(e.target.value))}
                          >
                            <option value="">Select a connection...</option>
                            {getRootConnectionsByType(integrationType).map((connection) => (
                              <option key={connection.integrationSettingsId} value={connection.integrationSettingsId}>
                                {connection.name} ({connection.vendor})
                              </option>
                            ))}
                          </select>
                        </div>

                        {selectedRootConnection && (
                          <>
                            <div className="mb-3">
                              <p className="text-sm text-gray-600 mb-3">
                                Override settings for: <strong>{selectedRootConnection.name}</strong>
                              </p>
                            </div>

                            <VendorOverrideFields
                              vendor={selectedRootConnection.vendor}
                              overrideValues={overrideValues}
                              onValueChange={handleOverrideValueChange}
                              overrideMode={true}
                              overrideFlags={overrideFlags}
                              onOverrideFlagChange={handleOverrideFlagChange}
                            />

                            <div className="flex justify-content-end gap-2 mt-4">
                              <Button label="Cancel" className="p-button-outlined" onClick={onSidebarHide} />
                              <Button label="Save Override" loading={isSaving} onClick={handleSaveOverride} />
                            </div>
                          </>
                        )}
                      </div>
                    </TabPanel>
                  </TabView>
                ) : (
                  <div className="p-3 p-fluid p-input-filled">
                    {/* Environment selection form */}
                    <div className="field mb-4">
                      <FormRadioGroup
                        control={control}
                        errors={formErrors}
                        field="environment"
                        label="Environment *"
                        options={EnvironmentOptions}
                      />
                    </div>

                    {/* Vendor selection form */}
                    <div className="field mb-4">
                      <FormDropdown
                        className="field"
                        control={control}
                        errors={formErrors}
                        field="vendor"
                        label={integrationType === IntegrationType.Fees ? 'Select a Fee Provider *' : 'Select a Vendor *'}
                        options={vendorOptions}
                        placeholder="Select a vendor"
                        showClear={true}
                      />
                    </div>

                    <div className="field mb-4">
                      <FormInputText
                        control={control}
                        errors={formErrors}
                        field="name"
                        label="Integration Name *"
                        placeholder="Enter a name for this integration"
                      />
                    </div>

                    {renderVendorSpecificSettings()}
                  </div>
                )}
              </BlockUI>
            </Sidebar>
          </>
        )}
    </>
  );

  return asPage ? (
    <AppPage title="Connection Settings" breadcrumbs="Connection Settings">
      <AppCard>{content}</AppCard>
    </AppPage>
  ) : (
    content
  );
};

export default ConnectionSettings;
